/**
 * HLS na obstoječem `<audio>` elementu (faza 6, D1).
 *
 * ⛔ **To ni samostojen predvajalnik in namenoma ne implementira `AudioPort`.**
 * D1 je postavko poimenoval `HlsPlayer`, a bi ločen razred podvojil vse, kar je
 * v `web-audio-player.ts` že izmerjeno na napravi: ponovni poskusi z zamikom,
 * straža živosti, tišina pred rezom, `swappingSource`, medijska seja. `hls.js`
 * se ne predvaja sam — priklopi se na **isti element** (`attachMedia`) in ga
 * hrani prek MSE. Zato je to priklop, ne predvajalnik.
 *
 * ---
 *
 * ⛔ **`canPlayType()` ne odloča** (D1). Vrne »maybe« tudi tam, kjer HLS ne
 * dela — v več Android WebView okoljih na `application/vnd.apple.mpegurl`
 * odgovori pritrdilno, predvajanje pa obstoji. Odloča **platforma**: WebKit
 * ima native HLS in tam je native prava pot (na iOS je celo edina, ki preživi
 * zaklenjen zaslon), povsod drugod gre tok skozi `hls.js`.
 *
 * ⛔ **Modul se naloži šele ob prvi HLS postaji.** `import()` je dinamičen, da
 * `hls.js` pristane v svojem svežnju: kdor HLS postaje nima, ga nikoli ne
 * prenese. To je prva runtime odvisnost projekta ([R1](../../../docs/01-research/R1-stack-decision.md))
 * in edina, ki si jo faza 6 privošči.
 */

type HlsModule = typeof import('hls.js/light');
type HlsInstance = InstanceType<HlsModule['default']>;

/**
 * Koliko odigranega naj hrani nazaj.
 *
 * Živ radio se ne premotava, zato je vsak zadržan segment porabljen pomnilnik.
 * 30 s je toliko, da prehod med segmenti ne zmanjka, in premalo, da bi na
 * telefonu kaj pomenilo.
 */
const BACK_BUFFER_S = 30;

export interface HlsHooks {
  /** Usodna napaka: tok je treba postaviti znova (zamik nosi klicatelj). */
  onFatal: () => void;
}

export interface HlsSession {
  destroy: () => void;
}

let modulePromise: Promise<HlsModule> | null = null;
let loaded: HlsModule | null = null;

/** Modul, če je že v pomnilniku — takrat priklop steče **sinhrono**. */
export function loadedHls(): HlsModule | null {
  return loaded;
}

/**
 * Naloži `hls.js` (lahka gradnja).
 *
 * ⚠️ Ob neuspehu se obljuba **pozabi**, da naslednji poskus ni obsojen na isti
 * zavrnjeni rezultat — prvi prenos lahko pade na omrežju, drugi ne.
 */
export function loadHls(): Promise<HlsModule> {
  if (modulePromise === null) {
    modulePromise = import('hls.js/light')
      .then((mod) => {
        loaded = mod;
        return mod;
      })
      .catch((error: unknown) => {
        modulePromise = null;
        throw error;
      });
  }
  return modulePromise;
}

/**
 * Ali gre HLS na tej platformi po **native** poti (čista funkcija, testabilna).
 *
 * iPadOS se od 13 naprej predstavlja kot `Macintosh`, zato ga loči večdotični
 * zaslon — ista zaznava kot pri `isIosWebKit()` v predvajalniku.
 */
export function prefersNativeHls(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return true;
  // Namizni Safari: ima native HLS in nima razloga za MSE. Chromium in Firefox
  // se oba predstavljata s `Safari` v nizu, zato jih je treba odšteti.
  return /Safari/.test(userAgent) && !/Chrome|Chromium|Android|Edg|OPR|Firefox/.test(userAgent);
}

/** Zaznava iz okolja; testi kličejo `prefersNativeHls()` neposredno. */
export function prefersNativeHlsHere(): boolean {
  if (typeof navigator === 'undefined') return false;
  return prefersNativeHls(navigator.userAgent, navigator.maxTouchPoints);
}

/**
 * Ali sme aplikacija za HLS računati na MSE — in s tem na Web Audio graf.
 *
 * 🔑 **Zakaj je to sploh vprašanje grafa.** Ko tok teče skozi `hls.js`, element
 * ne kaže na postajo, ampak na `blob:` z **našega izvora**. Medij zato ni
 * cross-origin, `createMediaElementSource()` ne odda ničel in EQ deluje **brez**
 * proxyja. Pri native HLS je obratno: element bere postajo neposredno in graf
 * bi utihnil, zato tam ostane izklopljen (T1 §4).
 */
export function hlsUsesMse(): boolean {
  if (prefersNativeHlsHere()) return false;
  return typeof MediaSource !== 'undefined';
}

/**
 * Priklopi `hls.js` na element in začni nalagati.
 *
 * Vrne `null`, kadar knjižnica na tem brskalniku ni podprta — takrat klicatelj
 * pade nazaj na navaden `src`, kar je zadnja poštena možnost.
 */
export function attachHls(
  mod: HlsModule,
  element: HTMLAudioElement,
  url: string,
  hooks: HlsHooks,
): HlsSession | null {
  const Hls = mod.default;
  if (!Hls.isSupported()) return null;

  const hls: HlsInstance = new Hls({
    /**
     * ⛔ **Brez delavca, in to je varnostna odločitev, ne zmogljivostna.**
     * `hls.js` delavca ustvari iz `blob:`, kar naš CSP (`default-src 'self'`)
     * zavrne — knjižnica se pobere sama, a v konzoli pusti kršitev ob vsakem
     * zagonu. Remultipleksiranje samega zvoka je poceni; delavec bi zahteval
     * `worker-src blob:`, torej širši CSP zaradi udobja knjižnice.
     */
    enableWorker: false,
    /** Nizka zakasnitev je za živ radio nepomembna in draga na mobilnem. */
    lowLatencyMode: false,
    backBufferLength: BACK_BUFFER_S,
  });

  hls.on(Hls.Events.ERROR, (_event, data) => {
    // Nefatalne napake knjižnica popravi sama (izpadel segment, prazna luknja).
    // Fatalne pomenijo, da tok stoji — od tam naprej je to naš ponovni poskus,
    // ki ima zamik in mejo; `hls.js` bi poskušal takoj in v nedogled.
    if (data.fatal) hooks.onFatal();
  });

  hls.attachMedia(element);
  hls.loadSource(url);

  return {
    destroy() {
      try {
        hls.destroy();
      } catch {
        // Razstavljanje ne sme podreti menjave postaje: element gre naprej.
      }
    },
  };
}
